import * as React from 'react';
import * as Images from 'pos/Images';

import { AvailableUserDTO, GetDeviceConfigurationResponse, NamedDiscountDTO, NamedDiscountProposalDTO, Res } from 'core/api';
import { Result } from 'core/infrastructure';
import { LoginFacade, RestaurantFacade } from 'pos/facades';
import { BackActionButton, Content, Footer, Header, PhoneOnly, PosScreen, UserButton } from 'pos/ui';
import { FingerprintReaderStatus } from 'pos/ui/FingerprintReaderStatus';
import { isPhone } from 'pos/ui/ThemeManager';
import { useFingerprintReaderRef } from 'pos/services/fingerprint';
import { ReaderStatus } from 'pos/common/BridgeProxy.Endpoints';
import getPassword from 'pos/modules/main/login/getPassword';
import { Format } from 'core/ui';
import { Message, Modal } from 'core/ui-next/modal';
import { useIsOnTop } from 'core/ui-next/modal/ModalHooks';
import { useBarcodeReaderRef } from 'core/infrastructure-next';
import { SvgIcon, Toaster } from 'core/ui-next';
import { Grid, HStack, Text, VStack } from '@chakra-ui/react';

type Params = {
	discount: NamedDiscountDTO;
	ticketLineId?: number;
	getProposal: (discountId: number, lineId: Nullable<number>) => Promise<Result<NamedDiscountProposalDTO>>;
}

export default async function getAuthorizerUserIdForDiscount(params: Params): Promise<Result<Nullable<number>>> {

	if (!params.discount.RequiresAuthorization)
		return Result.ok(null);

	const configuration = await RestaurantFacade.getDeviceConfiguration();
	const result = await params.getProposal(params.discount.Id, params.ticketLineId ?? null);
	if (!result.ok) {
		await Message.show(Res.Msg.MSG_NAMED_DISCOUNT_AUTHORIZATION_FAILURE());
		return Result.error();
	}

	const users = await RestaurantFacade.getNamedDiscountAuthorizers(params.discount.Id);
	return await Modal.show(SelectUserAuthorizeDiscountScreen,
		{
			users: users,
			proposal: result.value,
			configuration: configuration,
			requiresAuthentication: params.discount.RequiresAuthorizerAuthentication
		});
}

type Props = {
	users: AvailableUserDTO[],
	proposal: NamedDiscountProposalDTO,
	configuration: GetDeviceConfigurationResponse,
	requiresAuthentication: boolean,
} & Modal.Actions<number>;

function SelectUserAuthorizeDiscountScreen(props: Props) {

	const { users, proposal, configuration, requiresAuthentication, cancel, close } = props;

	// Es el createInitialState de la clase
	const [fingerPrintReaderStatus, setFingerPrintReaderStatus] = React.useState(Res.Msg.MSG_INIT_FINGER_PRINT_READER());

	// Es el campo lastFingerprintReaderFailure de la clase
	const lastFingerprintReaderFailure = React.useRef<string>('');


	const onStatusRead = React.useCallback(async (status: ReaderStatus) => {

		status.failure && status.failure !== lastFingerprintReaderFailure.current && Toaster.error(status.failure);
		status.message && setFingerPrintReaderStatus(status.message);

		lastFingerprintReaderFailure.current = status.failure;

		if (status.verifiedUserId !== null) {
			const user = users.find(x => x.Id === status.verifiedUserId);
			if (!user) {
				await Message.show(Res.Msg.MSG_NOT_VALID_AUTHORIZER_USER());
				return;
			}

			close(user.Id);
		}
	}, [users, close]);


	const onCardNumberRead = React.useCallback(async (cardNumber: string) => {

		const user = await LoginFacade.getUserByCardNumber(cardNumber);
		if (user.Id === 0) {
			await Message.show(Res.Msg.MSG_INVALID_USER_CODE());
			return;
		}

		const availableUser = users.find(x => x.Id === user.Id);
		if (!availableUser) {
			await Message.show(Res.Msg.MSG_NOT_VALID_AUTHORIZER_USER());
			return;
		}

		close(availableUser.Id);
	}, [users, close]);


	// Estos dos hooks son el startReaders y el stopReaders de la clase, igual que en loginCommand y en doClockingCommand:
	// arrancan los lectores al montar la pantalla (onComponentDidMount) y los paran al desmontarla
	// (onComponentWillUnmount), y además los paran y los vuelven a arrancar solos cuando esta pantalla deja de estar en
	// primer plano, que es exactamente lo que hacían a mano el stopReaders y el startReaders de alrededor del password.
	useBarcodeReaderRef(onCardNumberRead);
	useFingerprintReaderRef(onStatusRead);

	// El stopReaders de la clase olvidaba el último fallo del lector de huella para que, al volver a arrancarlo, se
	// vuelva a avisar aunque el fallo sea el mismo. Los lectores se paran al dejar de estar en primer plano, así que es
	// aquí donde toca olvidarlo.
	const isOnTop = useIsOnTop();
	React.useEffect(() => {
		if (!isOnTop)
			lastFingerprintReaderFailure.current = '';
	}, [isOnTop]);


	const onSelectUser = React.useCallback(async (user: AvailableUserDTO) => {

		// Si no requerimos autorización, no hace falta ni pedir el password. A lo mejor ya lo validan
		// con la firma del justificante
		if (!requiresAuthentication || !user.RequiresPassword) {
			close(user.Id);
			return;
		}

		// Mientras está abierta la pantalla del password, esta deja de estar en primer plano y los hooks de los lectores
		// los paran y los vuelven a arrancar al cerrarse, así que aquí ya no hay que hacer nada con ellos
		const ok = await confirmPassword(user);

		if (ok)
			close(user.Id);
	}, [requiresAuthentication, close]);

	// Es el cancelButton que devolvía getActionButtonsDefs. En tablet la pantalla se cierra con la X de la cabecera del
	// modal y en teléfono con el botón de volver del pie, que es lo que pinta el diseño nuevo.
	const onCancel = React.useCallback(async () => { cancel(); }, [cancel]);

	// Es el texto que la pantalla antigua cambiaba según el requiresAuthentication del descuento: si hay que autenticar
	// al usuario que autoriza se le pide que se identifique con el lector, y si no, que simplemente se seleccione de la
	// lista. El requiresAuthentication sigue decidiendo además si al pulsar sobre el usuario se le pide la contraseña.
	const identificationLabel = requiresAuthentication
		? Res.Web.GET_AUTHORIZED_USER_FOR_NAMED_DISCOUNT_USER_FINGERPRINT_LBL()
		: Res.Web.GET_AUTHORIZED_USER_FOR_NAMED_DISCOUNT_SELECT_USER_LBL();

	// getScreenId, getTitle, getImage y getSize de la clase pasan a ser los props del PosScreen y del Header. La pantalla
	// no tiene ningún campo numérico, así que no se pinta el keypad.
	return <PosScreen screenId={'SELECT_USER_TO_AUTHORIZE_DISCOUNT'} size={'medium'} useKeyPad={false}>
		<Header title={Res.Web.SELECT_USER_FOR_AUTHORIZE_DISCOUNT()} image={Images.SelectUser} onClose={onCancel} />
		<Content>
			<VStack width={'full'} height={'full'} gap={isPhone() ? '6' : '9'} paddingY={isPhone() ? '4' : '6'} paddingX={isPhone() ? '4' : '12'} bg={{ base: 'white', _dark: 'neutral.900' }}>
				{/* El justificante del descuento que se va a autorizar */}
				<HStack width={'full'} justifyContent={'center'} paddingX={isPhone() ? '0' : { base: '12', smDown: '4' }}>
					<NamedDiscountProposal namedDiscountProposal={proposal} />
				</HStack>
				<VStack width={'full'} gap={'4'}>
					<HStack width={'full'} justifyContent={'center'} paddingX={isPhone() ? '0' : { base: '12', smDown: '4' }}>
						<Text fontSize={'md'} fontWeight={'semibold'} textAlign={'center'}>
							{Res.Web.GET_AUTHORIZED_USER_FOR_NAMED_DISCOUNT_TITLE_LBL()}
						</Text>
					</HStack>
					<VStack width={'full'} paddingX={isPhone() ? '0' : { base: '12', smDown: '4' }} gap={'0'} textAlign={'center'}>
						<Text fontSize={'sm'}>{identificationLabel}</Text>
						<Text fontSize={'sm'}>{Res.Web.GET_AUTHORIZED_USER_FOR_NAMED_DISCOUNT_SELECT_RESPONSIBLE_USER_LBL()}</Text>
					</VStack>
				</VStack>
				<VStack width={'full'} height={'full'} overflowY={'auto'} gap={'0'}>
					{/* La lista de usuarios que pueden autorizar el descuento. El ancho máximo es el que hace que salgan cinco
				usuarios por fila en tablet (y cuatro en teléfono, donde manda el ancho del dispositivo), como en el
				diseño, y con el auto-fit las columnas que sobran no ocupan sitio, así que la rejilla queda centrada */}
					<Grid
						width={'full'}
						maxWidth={'40rem'}
						justifyContent={'center'}
						gap={{ base: '6', smDown: '2' }}
						paddingY={isPhone() ? '0' : '9'}
						paddingX={isPhone() ? '0' : { base: '0', smDown: '4' }}
						templateColumns={{ base: 'repeat(auto-fit, 6rem)', smDown: 'repeat(auto-fit, 4.75rem)' }}
					>
						{users.map(user => <UserButton key={user.Id} user={user} onSelect={onSelectUser} />)}
					</Grid>
				</VStack>
				{configuration.UseFingerprintReader
					? <>
						{/* El estado del lector de huella, que la pantalla antigua pintaba debajo del justificante. Sólo se ve si el
						punto de venta tiene lector configurado, así que en el diseño (que no lo tiene) no aparece */}
						<HStack width={'full'} justifyContent={!isPhone() ? 'flex-end' : 'center'} paddingX={{ base: '12', smDown: '4' }} paddingBottom={'4'} marginTop={'auto'}>
							<FingerprintReaderStatus enabled={configuration.UseFingerprintReader} message={fingerPrintReaderStatus} />
						</HStack>
					</>
					: null
				}
			</VStack>
		</Content>
		<Footer>
			<PhoneOnly>
				<BackActionButton action={onCancel} />
			</PhoneOnly>
		</Footer>
	</PosScreen >;
}

export async function confirmPassword(user: AvailableUserDTO): Promise<boolean> {
	const result = await getPassword(user);
	return result.ok;
}

// El justificante del descuento que se va a autorizar: el nombre del descuento y su importe, más el detalle de la línea
// (o del ticket) que llega en la descripción de la propuesta, que es la información que pintaba el NamedDiscountProposal
// de la pantalla antigua. El componente User que pintaba cada usuario de la lista ya no hace falta: el UserButton de
// pos/ui pinta la imagen del usuario (o sus iniciales con el color de fondo de su estilo) igual que hacía él.
function NamedDiscountProposal({ namedDiscountProposal }: { namedDiscountProposal: NamedDiscountProposalDTO }): React.ReactNode {

	// En teléfono no cabe el texto largo de la etiqueta del importe
	const amountLabel = isPhone()
		? Res.Web.GET_AUTHORIZED_USER_FOR_NAMED_DISCOUNT_DISCOUNT_AMOUNT_SHORT_LBL()
		: Res.Web.GET_AUTHORIZED_USER_FOR_NAMED_DISCOUNT_DISCOUNT_AMOUNT_LBL();

	// La descripción trae el producto y sus complementos separados por saltos de línea (y tabuladores)
	const description = namedDiscountProposal.DiscountDescription
		.split('\n')
		.map(line => line.trim())
		.filter(line => line !== '');

	return <VStack width={'full'} maxWidth={'30rem'} gap={'2'} alignItems={'stretch'}>
		<HStack
			width={'full'}
			gap={isPhone() ? '4' : '9'}
			padding={'4'}
			borderWidth={'1px'}
			borderColor={'neutral.300'}
			borderRadius={'md'}
		>
			<SvgIcon src={Images.DiscountLabel} w={'8'} h={'8'} color={{ base: 'neutral.700', _dark: 'white' }} />
			<HStack flex={'1'} minWidth={'0'} gap={'4'} alignItems={'stretch'}>
				<ProposalField label={Res.Web.GET_AUTHORIZED_USER_FOR_NAMED_DISCOUNT_DISCOUNT_NAME_LBL()} value={namedDiscountProposal.DiscountName} />
				<ProposalField label={amountLabel} value={Format.asMoney(namedDiscountProposal.CashDiscount)} withDivider={true} />
			</HStack>
		</HStack>

		<VStack width={'full'} gap={'0'} alignItems={'center'} textAlign={'center'}>
			{description.map((line, index) =>
				<Text key={index} fontSize={'xs'} color={{ base: 'neutral.500', _dark: 'neutral.400' }}>{line}</Text>
			)}
		</VStack>
	</VStack>;
}

function ProposalField({ label, value, withDivider }: { label: string, value: string, withDivider?: boolean }): React.ReactNode {

	return <VStack
		flex={'1'}
		minWidth={'0'}
		gap={'0.5'}
		alignItems={'flex-start'}
		borderLeftWidth={withDivider ? '1px' : undefined}
		borderColor={{ base: 'neutral.200', _dark: 'neutral.700' }}
		paddingLeft={withDivider ? '4' : undefined}
	>
		<Text width={'full'} fontSize={'sm'} color={{ base: 'secondary', _dark: 'white' }} truncate>{label}</Text>
		<Text width={'full'} fontSize={'md'} fontWeight={'semibold'} truncate>{value}</Text>
	</VStack>;
}
